import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material'
import { Stack, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import { ActionButton, DeleteButton, Editable, Picto } from '~/components'
import Field from './Field'
import { useParagraphContext } from './hooks'
import ParagraphTypeSelect from './ParagraphTypeSelect'

const ReadOnly = (props) => {
  const { children } = props
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false)

  return (
    <Tooltip
      disableHoverListener
      open={tooltipIsOpen}
      title={<Trans i18nKey="paragraph.read-only-title" />}
      onOpen={() => setTooltipIsOpen(true)}
      onClose={() => setTooltipIsOpen(false)}
    >
      <span
        onClick={() => {
          if (tooltipIsOpen) {
            setTooltipIsOpen(false)
          }
        }}
        onDoubleClick={() => setTooltipIsOpen(true)}
      >
        {children}
      </span>
    </Tooltip>
  )
}

const Title = (props) => {
  const { collapsed, readOnly: readOnlyProp, restorable } = props
  const { t } = useTranslation()
  const { editable, paragraph, onMoveDown, onMoveUp, removeParagraph } =
    useParagraphContext(Title.name)

  const isReadOnly = useMemo(
    () => readOnlyProp || paragraph.status?.indexOf('P') >= 0,
    [paragraph.status, readOnlyProp]
  )

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        flexGrow: 1,
        spacing: 1,
        minWidth: 0,
      }}
    >
      <Field
        editable={!collapsed}
        sx={{ flexGrow: 1, minWidth: 0 }}
        name="name"
        render={(fieldProps) => (
          <Editable
            disabled={!editable || isReadOnly}
            slots={isReadOnly ? { readOnly: ReadOnly } : undefined}
            sx={{ fontSize: '1.25rem' }}
            value={paragraph.name || t('paragraph.new')}
            variant="header"
            onStore={() => true}
            {...fieldProps}
          />
        )}
      />
      {!readOnlyProp && editable && (
        <>
          {collapsed && restorable && (
            <Tooltip title={t('paragraph.restorable')}>
              <Picto disabled letter="M" />
            </Tooltip>
          )}
          <Stack alignItems="center" direction="row" spacing={0.5}>
            {!collapsed && <ParagraphTypeSelect />}
            <ActionButton
              background
              disabled={!onMoveUp}
              icon={<ArrowUpwardIcon />}
              size="small"
              title={t('paragraph.move-up')}
              onClick={onMoveUp}
            />
            <ActionButton
              background
              disabled={!onMoveDown}
              icon={<ArrowDownwardIcon />}
              size="small"
              title={t('paragraph.move-down')}
              onClick={onMoveDown}
            />
            <DeleteButton
              dialog={{
                title: t('paragraph.delete.confirm.title'),
                body: t('paragraph.delete.confirm.body'),
              }}
              onConfirmed={removeParagraph}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}

Title.propTypes = {
  collapsed: PropTypes.bool,
  readOnly: PropTypes.bool,
  restorable: PropTypes.node,
}

export default Title
